import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

type Portal = {
  no: string; title: string; description: string | null;
  kalemler?: { code: string; name: string; unit: string; qty: number; not?: string | null }[];

  status: string; statusLabel: string; firma: string; contractor: string;
  sonrakiDurumlar: string[];
  files: { id: number; filename: string; size: number }[];
  gecmis: { statusLabel: string; note: string | null; createdAt: string }[];
};

const ETIKET: Record<string, string> = {
  KABUL: 'Kabul ettim', BASLADI: 'Başladım', BITTI: 'Bitirdim',
};
const boyut = (b: number) => (b < 1048576 ? `${Math.round(b / 1024)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const tarih = (s: string) =>
  new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' })
    .format(new Date(s));

/**
 * FASONCU PORTALI — şifresiz, mobil öncelikli.
 *
 * Oturum yok: yetki adresteki token. Yalnız kendi işini gösterir; fiyat,
 * maliyet, başka iş, cari listesi YOK. Tek sayfada iş detayı, dosyalar ve
 * üç düğme.
 */
export default function FasonPortal() {
  const { token = '' } = useParams();
  const [d, setD] = useState<Portal | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [not, setNot] = useState('');
  const [bekliyor, setBekliyor] = useState(false);
  const [tamam, setTamam] = useState<string | null>(null);

  const yukle = () =>
    fetch(`/api/f/${token}`)
      .then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.error ?? 'Bağlantı açılamadı');
        return j as Portal;
      })
      .then(setD)
      .catch((e) => setHata((e as Error).message));

  useEffect(() => { void yukle(); }, [token]);

  async function ilerlet(status: string) {
    setHata(null); setBekliyor(true);
    try {
      const r = await fetch(`/api/f/${token}/durum`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status, note: not || undefined }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? 'İşlem yapılamadı');
      setTamam(j.statusLabel);
      setNot('');
      await yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'İşlem yapılamadı');
    } finally {
      setBekliyor(false);
    }
  }

  return (
    <div className="ccd">
      <div className="ccd-portal">
        <div className="ccd-portal-kart ccd-stack">
          {hata && !d ? (
            <>
              <h1 className="ccd-title">Bağlantı açılamadı</h1>
              <div className="ccd-banner ccd-banner-danger">
                <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
              </div>
              <p className="ccd-meta">Art Mozaik & Seramik ile iletişime geçin.</p>
            </>
          ) : !d ? (
            <div className="ccd-inline-note">Yükleniyor…</div>
          ) : (
            <>
              <div>
                <div className="ccd-eyebrow">{d.firma}</div>
                <h1 className="ccd-title">{d.title}</h1>
                <div className="ccd-meta">
                  <span className="ccd-mono">{d.no}</span> · {d.contractor}
                </div>
              </div>

              <div>
                <span className="ccd-badge ccd-badge-info">{d.statusLabel}</span>
              </div>

              {d.kalemler && d.kalemler.length > 0 && (
                <div className="ccd-card ccd-stack-sm">
                  <b>Teslim edilen ürünler</b>
                  {d.kalemler.map((k, n) => (
                    <div key={n} className="ccd-row-between">
                      <span>{k.name}</span>
                      <b>{k.qty} {({ KUTU: 'kutu', M2: 'm²', ADET: 'adet', KG: 'kg', LT: 'lt', MTUL: 'metretül' } as Record<string, string>)[k.unit] ?? k.unit}</b>
                    </div>
                  ))}
                </div>
              )}
              {d.description && <div className="ccd-card">{d.description}</div>}

              {d.files.length > 0 && (
                <div className="ccd-card ccd-stack">
                  <div className="ccd-fieldset-title">Dosyalar</div>
                  {d.files.map((f) => (
                    <a key={f.id} className="ccd-btn ccd-btn-block"
                       href={`/api/f/${token}/dosya/${f.id}`}>
                      📎 {f.filename} · {boyut(f.size)}
                    </a>
                  ))}
                </div>
              )}

              {tamam && (
                <div className="ccd-banner">
                  <span className="ccd-banner-icon" aria-hidden="true">✓</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>{tamam} olarak kaydedildi.</div>
                    <div className="ccd-meta">Art Mozaik'e anında bildirildi.</div>
                  </div>
                </div>
              )}
              {hata && (
                <div className="ccd-banner ccd-banner-danger">
                  <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
                </div>
              )}

              {d.sonrakiDurumlar.length > 0 ? (
                <div className="ccd-card ccd-stack">
                  <div className="ccd-fieldset-title">Durumu güncelle</div>
                  <div className="ccd-field">
                    <input className="ccd-input" placeholder="Not (isteğe bağlı)"
                           value={not} onChange={(e) => setNot(e.target.value)} />
                  </div>
                  {d.sonrakiDurumlar.map((s) => (
                    <button key={s} className="ccd-btn ccd-btn-primary ccd-btn-lg ccd-btn-block"
                            disabled={bekliyor} onClick={() => ilerlet(s)}>
                      {bekliyor ? '…' : (ETIKET[s] ?? s)}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="ccd-empty">Bu iş için yapılacak bir adım kalmadı.</div>
              )}

              <div className="ccd-card">
                <div className="ccd-fieldset-title">Geçmiş</div>
                <div className="ccd-timeline">
                  {d.gecmis.map((e, i) => (
                    <div className="ccd-tl-item" key={i}>
                      <span className="ccd-tl-dot" />
                      <div className="ccd-tl-body">
                        <div className="ccd-tl-head">
                          <span className="ccd-tl-text">{e.statusLabel}</span>
                          <span className="ccd-tl-time">{tarih(e.createdAt)}</span>
                        </div>
                        {e.note && <div className="ccd-meta">{e.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
